import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LinesEllipsis from 'react-lines-ellipsis'
import { RouteComponentProps } from 'react-router-dom'
import { IonButtons, IonIcon } from '@ionic/react'
import { formatISO } from 'date-fns'
import { volumeHighOutline, volumeMuteOutline } from 'ionicons/icons'

import { Avatar } from '@components/atoms/Avatar'
import { UserName } from '@components/atoms/UserName'
import { VoteButton, VoteType } from '@components/atoms/VoteButton'
import { ReportButton } from '@components/molecules/ReportButton'
import { ShareButton } from '@components/molecules/ShareButton'
import { Share } from '@/components/atoms/assets/Share'
import { VotesLabel } from '@/components/atoms/VotesLabel'
import { CommentsButton } from '@/components/molecules/CommentsButton'

import { appConfig } from '@services/config'
import { useImageSizeV2 } from '@/lib/hooks/useImageSizeV2'
import { useMediaContainer } from '@/lib/hooks/useMediaContainer'
import { useTrackViewedActivity } from '@/lib/hooks/useTrackViewedActivity'
import { MediaType } from '@/lib/hooks/useTrackViewedActivity/useTrackViewedActivity'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { Context } from '@/lib/store'
import { ActionType } from '@/lib/store/reducer'
import { Tracking, TrackingEvent } from '@/lib/tracking'

import { ContestSections, ContestStatus, Pages, ReportButtonType } from '@enums'
import { IContestDetails, IContestUser } from '@/interfaces'

export interface IContestSlideProps {
  participant: IContestUser
  isContentVisible: boolean
  activeSliderIndex: number
  slideIndex: number
  authUserId: UUID | undefined
  contestDetails?: IContestDetails
  history: RouteComponentProps['history']
  channel?: string
  page?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tracking: any
  videoLoadedMilliseconds: number
}

export const ContestMobileWebSlide: FC<IContestSlideProps> = ({
  participant,
  isContentVisible,
  activeSliderIndex,
  slideIndex,
  authUserId,
  contestDetails,
  history,
  channel = '',
  page = '',
  tracking,
  videoLoadedMilliseconds,
}) => {
  const [upVote, setUpVote] = useState(participant?.votesCount || 0)
  const [isSameSlide, setIsSameSlide] = useState(true)
  const { t } = useTranslation('common')
  const { dispatch, state } = useContext(Context)
  const { isLandscape } = useImageSizeV2(participant?.video?.preview)

  const isParticipant = channel === ContestSections.Participant
  const isContestAvailable = contestDetails?.endDate > Date.now()

  const { data: authUser } = useUserInfo()
  const isActiveSlide = activeSliderIndex === slideIndex

  useEffect(() => {
    setTimeout(() => {
      setIsSameSlide(activeSliderIndex === slideIndex)
    }, 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSliderIndex])

  const { inView, mediaContainerRef } = useMediaContainer({
    ratio: 0.8,
    index: slideIndex,
  })

  useTrackViewedActivity({
    inViewport: inView,
    activityId: tracking.eventProperties?.challengeId,
    eventName: tracking.eventName,
    itemId: tracking.eventProperties?.submissionId,
    eventProperties: {
      ...tracking.eventProperties,
      videoLoadedDuration: videoLoadedMilliseconds,
      mediaType: MediaType.Video,
    },
    contentType: 'challenge',
    currentIndex: slideIndex,
  })

  const toggleMuteVideo = (e) => {
    e.stopPropagation()
    dispatch({
      type: ActionType.MUTE_VIDEO,
      payload: !state.muteVideo,
    })
  }

  const trackChallengeProperties = {
    challengeName: contestDetails?.name,
    challengeId: contestDetails?.id,
  }

  const handleShareClick = useCallback(() => {
    if (page === Pages.Contest) {
      isParticipant
        ? Tracking.triggerEvent(TrackingEvent.SubmissionShared, {
            activityId: participant?.activityId,
            contentId: participant?.id,
            contentType: 'participant',
            contentOwnerId: participant?.user?.id,
            userId: authUser?.id,
            seasonId: contestDetails?.id,
            timestamp: formatISO(new Date()),
          })
        : Tracking.triggerEvent(TrackingEvent.ChallengeCreatorVideoShared, {
            activityId: contestDetails?.activityId,
            contentId: contestDetails?.id,
            contentType: 'challenge',
            contentOwnerId: contestDetails?.creator?.user?.id,
            userId: authUser?.id,
            timestamp: formatISO(new Date()),
          })
    }
  }, [
    page,
    participant?.activityId,
    participant?.id,
    participant?.user?.id,
    contestDetails?.activityId,
    contestDetails?.id,
    contestDetails?.creator?.user?.id,
    authUser?.id,
    isParticipant,
  ])

  return (
    <div className="w-full h-full" ref={mediaContainerRef}>
      <div
        className={`absolute right-4 z-20 w-10 top-20 h-10 bg-zinc-500 opacity-70 rounded-full flex items-center justify-center`}
        onClick={(e) => toggleMuteVideo(e)}
      >
        <IonIcon
          icon={state.muteVideo ? volumeMuteOutline : volumeHighOutline}
        />
      </div>
      <div
        onClick={(e) => toggleMuteVideo(e)}
        className="flex relative overflow-hidden h-full w-screen items-center justify-center"
        style={{
          opacity: isContentVisible ? 0 : 1,
          transitionDelay: `${isContentVisible && !isSameSlide ? 1 : 0}s`,
          transitionDuration: '0',
          transitionProperty: 'opacity',
          transitionTimingFunction: 'ease-out',
          backgroundColor: isLandscape ? 'black' : '',
        }}
      >
        <img
          className="absolute object-cover top-1/2 object-center -translate-y-1/2"
          src={participant.video.preview}
          style={{
            pointerEvents: 'none',
            height: 'auto',
            width: '100%',
          }}
        />
      </div>

      <div className="absolute bottom-0 pl-4 pr-3 pb-[1.875rem] flex justify-between items-end w-full z-10 bg-gradient-to-t from-black/60">
        <div className="flex flex-col">
          {participant.user && (
            <div className="flex items-center pb-1">
              <Avatar
                className="mr-1"
                username={participant.user?.username}
                userId={participant.user.id}
                isModerator={participant.user?.isModerator}
                isAmbassador={participant.user?.isAmbassador}
                badge={participant.user?.badge}
                clickable
              />
              <div className="text-left">
                <VotesLabel count={upVote} />
                <UserName
                  medium
                  name={participant.user.username}
                  userId={participant.user.id}
                />
              </div>
            </div>
          )}
          {!!participant.description && (
            <LinesEllipsis
              className="text-left text-sm font-normal ml-10 whitespace-pre-line break-large"
              text={participant.description}
              maxLine={3}
              basedOn="letters"
              ellipsis={
                <span
                  onClick={() =>
                    participant?.activityId
                      ? history.push(`/comments/${participant?.activityId}`)
                      : null
                  }
                  className="whitespace-normal"
                >
                  ... {!!participant?.activityId && t('common.more')}
                </span>
              }
            />
          )}
          {isContestAvailable &&
            (contestDetails.status === ContestStatus.SubmissionVoting ||
              contestDetails.status === ContestStatus.Voting) &&
            participant?.id &&
            authUserId !== participant?.id && (
              <VoteButton
                onVote={() => {
                  Tracking.triggerEvent(TrackingEvent.SubmissionVoted, {
                    ...trackChallengeProperties,
                    submissionId: participant?.id,
                  })
                  setUpVote((count) => count + 1)
                }}
                contestantId={participant?.id}
                voteType={VoteType.Upvote}
                isActiveSlide={isActiveSlide}
              />
            )}
        </div>
        <div className="w-12 flex flex-col items-end">
          <IonButtons className="block mt-2">
            {participant?.activityId && (
              <CommentsButton
                activityId={participant?.activityId}
                commentsNumber={
                  contestDetails?.creator.user.id === participant.user.id
                    ? contestDetails?.commentsCount
                    : participant?.commentsCount
                }
                commentsNumberPosition="bottom"
                channel={channel}
                page={page}
                participantId={participant?.id}
              />
            )}
            <ShareButton
              className="flex justify-center items-end mt-2"
              subject={contestDetails?.name}
              message={t('contest.videoBy', {
                userName: participant?.user?.username,
              })}
              buttonLabel={<Share color="success" />}
              url={`${appConfig.origin}/contest/${contestDetails?.id}/video/${participant?.video.id}`}
              imageUrl={participant.video.preview}
              feature="contest-video"
              params={{ refUsername: String(authUser?.username) }}
              onClickShare={handleShareClick}
            />
            {participant?.user.id !== authUserId && (
              <ReportButton
                type={ReportButtonType.Video}
                confirmationLabel={t('okay')}
                contestId={contestDetails?.id}
                userId={participant?.video?.id}
                activityId={participant?.activityId}
                isDisabled={false}
              />
            )}
          </IonButtons>
        </div>
      </div>
    </div>
  )
}
